import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {
  private static readonly SCRIPT_ID = 'recaptcha-enterprise-script';
  private static scriptLoadPromise: Promise<void> | null = null;
  private readonly siteKey = environment.recaptchaSiteKey;

  constructor(@Inject(DOCUMENT) private document: Document) {
    if (this.siteKey) {
      void this.ensureApiLoaded();
    }
  }

  async generateToken(action: string): Promise<string> {
    if (!this.siteKey) {
      return '';
    }

    try {
      await this.ensureApiLoaded();
    } catch {
      throw new Error('Falha ao carregar a API do reCAPTCHA Enterprise.');
    }

    return this.executeAction(action);
  }

  private ensureApiLoaded(): Promise<void> {
    if (!this.siteKey) {
      return Promise.resolve();
    }

    if (window.grecaptcha?.enterprise) {
      return Promise.resolve();
    }

    if (RecaptchaService.scriptLoadPromise) {
      return RecaptchaService.scriptLoadPromise;
    }

    RecaptchaService.scriptLoadPromise = new Promise<void>((resolve, reject) => {
      const existingScript = this.document.getElementById(RecaptchaService.SCRIPT_ID) as HTMLScriptElement | null;

      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Failed to load existing reCAPTCHA script.')), { once: true });
        return;
      }

      const script = this.document.createElement('script');
      script.id = RecaptchaService.SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(this.siteKey)}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load reCAPTCHA script.'));

      this.document.head.appendChild(script);
    }).catch((error) => {
      RecaptchaService.scriptLoadPromise = null;
      throw error;
    });

    return RecaptchaService.scriptLoadPromise;
  }

  private async executeAction(action: string): Promise<string> {
    const enterprise = window.grecaptcha?.enterprise;

    if (!enterprise) {
      throw new Error('API do reCAPTCHA Enterprise indisponivel.');
    }

    await new Promise<void>((resolve) => {
      enterprise.ready(() => resolve());
    });

    try {
      return await enterprise.execute(this.siteKey, { action });
    } catch {
      throw new Error('Falha ao gerar token do reCAPTCHA Enterprise.');
    }
  }
}
